import React, { createContext, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import isClient from '../common/helpers/environment';
import { useLogger } from './logger/LoggerProvider';
import StateInterface from './redux/types';
import useGa from './hooks/useGa';
import { FeatureToggles } from 'Common/appshell_types/featureToggles';
import { Logger } from 'Common/appshell_types/loggerTypes';

export interface LaunchDarklyEvent {
    type: 'variation' | 'metric';
    name: string;
}

// max time in ms after an event is received before it is sent to backend
const trackingPauseTime = 2000;
const localStorageKey = 'bella-launch-darkly-events';
const fallbackSessionId = 'FALLBACK_SESSION_ID';
const fallbackJounreyId = 'FALLBACK_JOURNEY_ID';

export interface GenericLaunchDarklyTracker {
    trackMetric: (metricKey: string) => void;
    trackVariation: (toggle: keyof FeatureToggles | string, toggleVal?: boolean | string) => void;
    setLogger: (logger: Logger) => void;
    trackImportantMetric: (metricKey: string) => void;
    setUserInfo: (userInfo: UserInfo) => void;
    setGATrackingFn: (fn: GATrackingFN) => void;
}

interface UserInfo {
    userId?: string;
    key?: string;
    loginHash?: string;
    sessionId?: string;
    journeyId: string;
}

type GATrackingFN = (a: keyof FeatureToggles | string, b: string | boolean) => void;
export class LaunchDarklyTracker implements GenericLaunchDarklyTracker {
    private logger: Logger;

    private userId?: string;

    private key?: string;

    private sessionId?: string;

    private journeyId?: string = fallbackJounreyId;

    private loginHash?: string;

    private gaTrackingFn?: GATrackingFN;

    private addToQueue(evt: LaunchDarklyEvent) {
        try {
            const eventsJSON = localStorage.getItem(localStorageKey) || '[]';
            const events = JSON.parse(eventsJSON);
            events.push(evt);
            localStorage.setItem(localStorageKey, JSON.stringify(events));
            setTimeout(() => {
                this.processQue();
            }, trackingPauseTime);

            window.seenLaunchDarklyTrackingEvents = [...(window.seenLaunchDarklyTrackingEvents || []), evt];
        } catch (e) {
            this.logger.error(e, { message: '[LaunchDarkly]problem adding to darkly event queue' });
        }
    }

    public trackMetric(metricKey: string) {
        return;
        // @TODO this should be removed when we remove complete LD tracking
        // eslint-disable-next-line no-unreachable
        this.addToQueue({
            type: 'metric',
            name: metricKey,
        });
    }

    /**
     * Add metric event to que and process que right away
     * @param metricKey key matching metric in LD
     */
    public trackImportantMetric(metricKey: string) {
        return;
        // @TODO this should be removed when we remove complete LD tracking
        // eslint-disable-next-line no-unreachable
        this.trackMetric(metricKey);
        this.processQue();
    }

    public trackVariation(toggle: keyof FeatureToggles | string, toggleVal?: boolean | string) {
        if (typeof toggleVal === 'undefined') {
            this.logger.error('[LaunchDarkly] trackVariation - An undefined Feature Toggle received', {
                sessionId: this.sessionId,
                toggle,
            });
            return;
        }
        this.trackWithGA(toggle, toggleVal);
        return;
        // @TODO this should be removed when we remove complete LD tracking
        // eslint-disable-next-line no-unreachable
        this.addToQueue({
            type: 'variation',
            name: toggle,
        });
    }

    public setUserInfo(userInfo: UserInfo) {
        if (userInfo?.userId) {
            this.userId = userInfo.userId;
        }

        if (userInfo?.sessionId) {
            this.sessionId = userInfo.sessionId;
        }

        if (userInfo?.loginHash) {
            this.loginHash = userInfo.loginHash;
        }

        if (userInfo?.key) {
            this.key = userInfo.key;
        }

        if (userInfo?.journeyId) {
            this.journeyId = userInfo.journeyId;
        }
    }

    private trackWithGA(toggle: keyof FeatureToggles | string, toggleVal: boolean | string) {
        if (this.gaTrackingFn) {
            let finalToggleVal = toggleVal;
            if (typeof toggleVal === 'boolean') {
                finalToggleVal = toggleVal ? 'true' : 'false';
            }
            this.gaTrackingFn(toggle, finalToggleVal);
        }
    }

    public setGATrackingFn(fn: GATrackingFN) {
        this.gaTrackingFn = fn;
    }

    private processQue() {
        let events: Array<LaunchDarklyEvent> = [];
        try {
            const eventsJSON = localStorage.getItem(localStorageKey) || '[]';
            events = JSON.parse(eventsJSON);
            if (events.length === 0) {
                return;
            }
        } catch {
            this.logger.error('[LaunchDarkly]Problem parsing darkly events from local storage');
        }

        const user = {
            journeyId: this.journeyId,
            ...(this.userId ? { userId: this.userId } : {}),
            ...(this.sessionId ? { sessionId: this.sessionId } : {}),
            ...(this.loginHash ? { loginHash: this.loginHash } : {}),
            ...(this.key ? { key: this.key } : {}),
        };

        if (this.sessionId === fallbackSessionId) {
            this.logger.error('[LaunchDarkly]Darkly event seen with fallback session id', { events, user });
            return;
        }

        fetch('/bella-api/launchdarkly-tracking', {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                events,
                user,
            }),
        }).catch(e => {
            this.logger.error('[LaunchDarkly]Problem sending darkly tracking event', {
                user,
                events,
                message: e.message,
            });
        });

        localStorage.setItem(localStorageKey, '[]');
    }

    public setLogger(logger: Logger) {
        this.logger = logger;
    }
}

const LaunchDarklyTrackerContext = createContext<GenericLaunchDarklyTracker | undefined>(undefined);

export const LaunchDarklyTrackerProvider: React.FC = ({ children }) => {
    const logger = useLogger();
    let launchDarklyTracker;

    if (isClient()) {
        launchDarklyTracker = new LaunchDarklyTracker();
        launchDarklyTracker.setLogger(logger);
    }

    return (
        <LaunchDarklyTrackerContext.Provider value={launchDarklyTracker}>
            {children}
        </LaunchDarklyTrackerContext.Provider>
    );
};

/**
 * Context with launchDarklyTracker.
 *
 * We don't put the DarklyTrackerProvider on the server-side
 * so it is possible for this to return undefined
 */
export const useLaunchDarklyTracker = (): GenericLaunchDarklyTracker => {
    const [cookies] = useCookies();
    const launchDarklyTracker = useContext(LaunchDarklyTrackerContext);
    const userId = useSelector<StateInterface, string | undefined>(state => state.appShellLogin?.data?.id);
    const loginHash = useSelector<StateInterface, string | undefined>(state => state.appShellLogin?.data?.loginHash);
    const launchDarklySessionKey = useSelector<StateInterface, string>(
        state => state.appShellLogin?.launchDarklySessionKey
    );
    const gaTracking = useGa();

    useEffect(() => {
        /**
         * Preference of LD session key/ID
         * 1. Key From server side data (!== fallbackSessionId)
         * 2. Login hash
         * 3. ww_jid cookie
         * 4. FALLBACK_SESSION_ID
         */
        const journeyId = cookies.ww_jid || fallbackJounreyId;
        const userInfo: UserInfo = { userId, loginHash, journeyId };
        if (launchDarklySessionKey === fallbackSessionId) {
            userInfo.sessionId = loginHash || cookies.ww_jid || fallbackSessionId;
        } else {
            userInfo.key = launchDarklySessionKey;
        }

        if (launchDarklyTracker) {
            launchDarklyTracker.setUserInfo(userInfo);
        }
        // We need to setUserInfo only when following changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [launchDarklyTracker, loginHash, userId, launchDarklySessionKey]);

    useEffect(() => {
        if (launchDarklyTracker) {
            launchDarklyTracker.setGATrackingFn((toggleName, toggleValue: string) =>
                gaTracking.trackLaunchDarklyVariation(toggleName, `${toggleValue} user-key: ${launchDarklySessionKey}`)
            );
        }
    }, [launchDarklyTracker, gaTracking, launchDarklySessionKey]);

    if (launchDarklyTracker) {
        return launchDarklyTracker;
    }

    return {
        trackMetric: () => null,
        trackVariation: () => null,
        setLogger: () => null,
        trackImportantMetric: () => null,
        setUserInfo: () => null,
        setGATrackingFn: () => null,
    };
};
